// components/admin/ContactDetailsModal.jsx
import { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../context/authContext";
import { createContact, updateContact } from "../../services/contactsService";
import { Edit, Save, X, Eye, Mail, Phone, User, Briefcase, Building, CreditCard } from "lucide-react";
import Swal from "sweetalert2";
import { ActionLoader } from "../loader";
import ModalTitle from "../common/ModalTitle";

const ContactSchema = Yup.object().shape({
  contact_name: Yup.string()
    .required("El nombre es obligatorio")
    .min(2, "El nombre debe tener al menos 2 caracteres")
    .max(50, "El nombre no puede tener más de 50 caracteres"),
  email: Yup.string()
    .email("Email inválido")
    .matches(/^[^@]+@[^@]+\.[^@]+$/, "Email inválido: debe tener un dominio válido")
    .required("El email es obligatorio"),
  phone_number: Yup.string().required('Teléfono móvil es obligatorio'),
  title_job: Yup.string().required("El cargo es obligatorio"),
  area: Yup.string().required("El área es obligatoria"),
  cobranza: Yup.boolean(),
});

const ContactDetailsModal = ({ isOpen, onClose, contact, companyName, mode = "view" }) => {
  const { user } = useAuth();
  const [currentMode, setCurrentMode] = useState(mode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      setCurrentMode(mode);
    }
  }, [isOpen, mode]);
  
  const handleSwitchMode = () => {
    setCurrentMode(currentMode === "view" ? "edit" : "view");
  };

  const getModalTitle = () => {
    if (currentMode === "create") return "Crear Contacto";
    if (currentMode === "edit") return "Editar Contacto";
    return "Detalles del Contacto";
  };

  const initialValues = contact
    ? {
        contact_name: contact.contact_name || "",
        email: contact.email || "",
        phone_number: contact.phone_number || "",
        title_job: contact.title_job || "",
        area: contact.area || "",
        cobranza: contact.cobranza || false,
      }
    : {
        contact_name: "",
        email: "",
        phone_number: "",
        title_job: "",
        area: "",
        cobranza: false,
      };

  const handleSubmit = async (values) => {
    if (!companyName) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se ha seleccionado una empresa"
      });
      return;
    }

    try {
      const confirmation = await Swal.fire({
        title: "Confirmación",
        text: "¿Está seguro de que desea guardar los cambios?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#0488AF",
        cancelButtonColor: "#C42251",
        confirmButtonText: "Sí, guardar",
        cancelButtonText: "Cancelar"
      });
      if (!confirmation.isConfirmed) return;
      setIsSubmitting(true);
      
      // Añadir el business_name a los datos a enviar
      const contactData = {
        ...values,
        business_name: companyName
      };

      if (contact?.id) {
        // Actualizar contacto existente
        await updateContact(contact.id, contactData, user);
        Swal.fire({
          icon: "success",
          title: "Actualizado",
          text: "Contacto actualizado correctamente",
          timer: 1500
        });
      } else {
        // Crear nuevo contacto
        await createContact(contactData, user);
        Swal.fire({
          icon: "success",
          title: "Creado",
          text: "Contacto creado correctamente",
          timer: 1500
        });
      }
      
      // Cerrar modal y refrescar lista
      onClose(true);
    } catch (error) {
      console.error("Error saving contact:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "No se pudo guardar el contacto"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header con gradiente mejorado */}
        <div className="relative bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white">
          <div className="px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">{getModalTitle()}</h2>
                {companyName && (
                  <p className="text-[#0488AF]/70 mt-1 text-sm">
                    Empresa: <span className="font-medium text-white">{companyName}</span>
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                {currentMode !== "create" && (
                  <button
                    type="button"
                    onClick={handleSwitchMode}
                    className="flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-4 py-2 rounded-xl transition-all duration-200 hover:scale-105"
                  >
                    {currentMode === "view" ? (
                      <>
                        <Edit size={18} /> 
                        <span className="hidden sm:inline">Editar</span>
                      </>
                    ) : (
                      <>
                        <Eye size={18} /> 
                        <span className="hidden sm:inline">Ver</span>
                      </>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onClose()}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm p-2 rounded-xl transition-all duration-200 hover:scale-105"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Contenido del modal */}
        <div className="p-8 overflow-y-auto max-h-[calc(90vh-140px)]">
          <Formik
            initialValues={initialValues}
            validationSchema={ContactSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ errors, touched, isValid, values, setFieldValue }) => (
              <Form className="space-y-8">
                {/* Información Personal */}
                <div className="bg-gradient-to-br from-[#E6F2F9] to-white rounded-2xl p-6 border border-[#0488AF]/20 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-[#0488AF]/20 p-2 rounded-xl">
                      <User className="text-[#0488AF]" size={20} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">Información Personal</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Nombre Completo <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <Field
                          name="contact_name"
                          type="text"
                          disabled={currentMode === "view"}
                          placeholder="Ingrese el nombre completo"
                          className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] transition-all duration-200 ${
                            currentMode === "view" 
                              ? "bg-gray-50 border-gray-200 cursor-not-allowed text-gray-600" 
                              : "bg-white border-gray-300 hover:border-gray-400"
                          } ${errors.contact_name && touched.contact_name ? "border-red-500 ring-2 ring-red-200" : ""}`}
                        />
                      </div>
                      <ErrorMessage name="contact_name" component="div" className="text-sm text-red-600 mt-1" />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <Field
                          name="email"
                          type="email"
                          disabled={currentMode === "view"}
                          placeholder="contacto@example.com"
                          className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] transition-all duration-200 ${
                            currentMode === "view" 
                              ? "bg-gray-50 border-gray-200 cursor-not-allowed text-gray-600" 
                              : "bg-white border-gray-300 hover:border-gray-400"
                          } ${errors.email && touched.email ? "border-red-500 ring-2 ring-red-200" : ""}`}
                        />
                      </div>
                      <ErrorMessage name="email" component="div" className="text-sm text-red-600 mt-1" />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Teléfono <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <Field
                          name="phone_number"
                          type="text"
                          disabled={currentMode === "view"}
                          placeholder="+56 9 12345678"
                          className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] transition-all duration-200 ${
                            currentMode === "view" 
                              ? "bg-gray-50 border-gray-200 cursor-not-allowed text-gray-600" 
                              : "bg-white border-gray-300 hover:border-gray-400"
                          } ${errors.phone_number && touched.phone_number ? "border-red-500 ring-2 ring-red-200" : ""}`}
                        />
                      </div>
                      <ErrorMessage name="phone_number" component="div" className="text-sm text-red-600 mt-1" />
                    </div>
                  </div>
                </div>

                {/* Información Laboral */}
                <div className="bg-gradient-to-br from-[#DFF6FA] to-white rounded-2xl p-6 border border-[#0488AF]/30 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-[#0488AF]/20 p-2 rounded-xl">
                      <Briefcase className="text-[#0488AF]" size={20} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">Información Laboral</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Cargo <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <Field
                          name="title_job"
                          type="text"
                          disabled={currentMode === "view"}
                          placeholder="Ej: Gerente de Ventas"
                          className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#09274A] focus:border-[#09274A] transition-all duration-200 ${
                            currentMode === "view" 
                              ? "bg-gray-50 border-gray-200 cursor-not-allowed text-gray-600" 
                              : "bg-white border-gray-300 hover:border-gray-400"
                          } ${errors.title_job && touched.title_job ? "border-red-500 ring-2 ring-red-200" : ""}`}
                        />
                      </div>
                      <ErrorMessage name="title_job" component="div" className="text-sm text-red-600 mt-1" />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Área <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <Field
                          name="area"
                          type="text"
                          disabled={currentMode === "view"}
                          placeholder="Ej: Ventas, Marketing, IT"
                          className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#09274A] focus:border-[#09274A] transition-all duration-200 ${
                            currentMode === "view" 
                              ? "bg-gray-50 border-gray-200 cursor-not-allowed text-gray-600" 
                              : "bg-white border-gray-300 hover:border-gray-400"
                          } ${errors.area && touched.area ? "border-red-500 ring-2 ring-red-200" : ""}`}
                        />
                      </div>
                      <ErrorMessage name="area" component="div" className="text-sm text-red-600 mt-1" />
                    </div>
                  </div>
                </div>

                {/* Configuración de Cobranza */}
                <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-6 border border-green-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-[#0488AF]/10 p-2 rounded-xl">
                      <CreditCard className="text-[#0488AF]" size={20} />
                    </div>

                    <h3 className="text-lg font-semibold text-gray-800">Configuración Financiera</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200">
                      <div className="flex items-center gap-3">
                        <CreditCard className="text-green-600" size={20} />
                        <div>
                          <label className="block text-md font-medium text-gray-700">
                            Contacto de Cobranza
                          </label>
                          <p className="text-sm font-extrabold mt-1">
                            A este contacto se le enviarán notificaciones de cobranza.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <Field name="cobranza">
                          {({ field }) => (
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                {...field}
                                checked={values.cobranza}
                                disabled={currentMode === "view"}
                                onChange={(e) => setFieldValue("cobranza", e.target.checked)}
                                className="sr-only"
                              />
                              <div className={`relative w-14 h-7 rounded-full transition-colors duration-200 ${
                                values.cobranza ? "bg-[#0488AF]" : "bg-gray-300"
                              } ${currentMode === "view" ? "opacity-50 cursor-not-allowed" : ""}`}>
                                <div className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full transition-transform duration-200 ${
                                  values.cobranza ? "translate-x-7" : "translate-x-0"
                                }`}></div>
                              </div>
                              <span className={`ml-3 text-sm font-medium ${
                                values.cobranza ? "text-[#09274A]" : "text-gray-500"
                              }`}>
                                {values.cobranza ? "Sí" : "No"}
                              </span>
                            </label>
                          )}
                        </Field>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feedback de errores */}
                {!isValid && Object.keys(errors).length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-red-100 p-2 rounded-xl">
                        <X className="text-red-600" size={20} />
                      </div>
                      <h4 className="font-semibold text-red-700">Errores de validación</h4>
                    </div>
                    <ul className="list-disc list-inside text-red-600 text-sm space-y-1">
                      {Object.entries(errors).map(([field, errorMsg]) => (
                        <li key={field}>{errorMsg}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Botones de acción */}
                <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => onClose()}
                    className="px-6 py-3 border border-[#DC3465] bg-[#DC3465] text-white rounded-xl hover:bg-[#C42251] transition-colors duration-200 font-medium"
                  >
                    Cancelar
                  </button>

                  {currentMode !== "view" && (
                    <button
                      type="submit"
                      disabled={isSubmitting || !isValid}
                      className={`px-8 py-3 rounded-xl flex items-center justify-center gap-2 font-medium transition-all duration-200 ${
                        isSubmitting || !isValid
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-[#1BBB55] text-white hover:bg-[#1aab51] hover:shadow-lg transform hover:scale-105"
                      }`}
                    >
                      <Save size={18} />
                      {isSubmitting ? "Guardando..." : "Guardar Contacto"}
                    </button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <ActionLoader open={isSubmitting} onClose={() => {}} />
    </div>
  );
}

export default ContactDetailsModal;